import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

interface NearbyDevice {
  device_id: string;
  device_code: string;
  device_name: string;
  location: string | null;
  distance_meters: number;
}

interface GeoPosition {
  lat: number;
  lon: number;
}

export function useNearbyDevices() {
  const [position, setPosition] = useState<GeoPosition | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation stöds inte av din enhet');
      return;
    }

    setLocating(true);
    setGeoError(null);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setLocating(false);
      },
      (err) => {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setGeoError('Platsåtkomst nekad. Tillåt platsåtkomst i inställningarna.');
            break;
          case err.POSITION_UNAVAILABLE:
            setGeoError('Din position kunde inte bestämmas');
            break;
          case err.TIMEOUT:
            setGeoError('Tidsgräns nådd vid positionsbestämning');
            break;
          default:
            setGeoError('Ett oväntat fel uppstod');
        }
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  }, []);

  const { data: nearbyDevices, isLoading: loadingDevices } = useQuery({
    queryKey: ['nearby-devices', position?.lat, position?.lon],
    queryFn: async (): Promise<NearbyDevice[]> => {
      if (!position) return [];

      const { data, error } = await supabase.rpc('get_nearby_public_devices', {
        p_lat: position.lat,
        p_lon: position.lon,
        p_radius_meters: 500,
      });

      if (error) throw error;
      return (data ?? []) as NearbyDevice[];
    },
    enabled: !!position,
    staleTime: 30 * 1000,
  });

  return {
    position,
    nearbyDevices: nearbyDevices ?? [],
    geoError,
    locating,
    loadingDevices,
    requestLocation,
  };
}
